import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileDown, Loader2, Trash2, RotateCcw, AlertTriangle, EyeOff, Eye } from "lucide-react";
import { PrixMPDialog } from "@/components/PrixMPDialog";
import { PrixVenteDialog } from "@/components/PrixVenteDialog";
import { EntreesMPDialog } from "@/components/EntreesMPDialog";
import { StocksAnnexesDialog } from "@/components/StocksAnnexesDialog";

const nf = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 }).format(n || 0);
const dfr = (d) => d.split("-").reverse().join("/");
const COUTS = new Set(["Coût revient / u", "Coût de revient ventes", "Coût de revient total", "Marge brute"]);
const MasqueCtx = createContext(false);

const Tbl = (props) => {
  const masque = useContext(MasqueCtx);
  const keep = props.head.map((h) => !(masque && COUTS.has(h.replace("§", ""))));
  const f = (r) => r.filter((_, i) => keep[i]);
  return <TblBase {...props} head={f(props.head)} rows={props.rows.map(f)} foot={props.foot.map(f)} />;
};

const TblBase = ({ head, rows, foot, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm min-w-[720px]">
      <thead><tr className="text-left text-muted-foreground border-b">
        {head.map((h, i) => <th key={h} className={`py-2 pr-3 font-medium ${h.endsWith("§") ? "text-right" : ""}`}>{h.replace("§", "")}</th>)}
      </tr></thead>
      <tbody className="divide-y">
        {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className={`py-2 pr-3 ${head[j].endsWith("§") ? "tabular text-right" : ""}`}>{c}</td>)}</tr>)}
        {foot.map((r, i) => (
          <tr key={`f${i}`} className={`font-bold ${i === 0 ? "border-t-2 border-primary/30" : ""} bg-primary/5`}>
            {r.map((c, j) => <td key={j} className={`py-2 pr-3 ${head[j].endsWith("§") ? "tabular text-right" : ""}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl border bg-card p-5 mt-6">
    <h3 className="font-heading font-bold mb-3">{title}</h3>
    {children}
  </div>
);

const opt = (v, f) => (v === null || v === undefined ? "—" : f(v));

const StocksAnnexes = ({ rows, annee, masque, edit }) => (
  <Section title={`Emballages et autres matières premières — stock début et fin ${annee}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <p className="text-xs text-muted-foreground">Stock 31/12 = comptage réel s'il est saisi, sinon stock début + achats − consommation. * Achats non saisis = quantité consommée par la production (+ stock compté − stock début).</p>
      {edit}
    </div>
    <TblBase testid="etat-stock-annexes-table"
      head={["Article", "Type", "Stock 01/01§", "Achats§", "Conso. production§", "Stock 31/12§", "Base", "Écart§", ...(masque ? [] : ["Valeur stock HT§"])]}
      rows={rows.map((r) => [`${r.mp} (${r.unite})`, r.type, nf(r.debut), r.achats_saisi ? nf(r.achats) : <span title="Selon la production">{nf(r.achats)}<span className="text-muted-foreground">*</span></span>, nf(r.consommation), nf(r.stock_fin),
        <span className={r.base === "Comptage" ? "text-emerald-700" : ""}>{r.base}</span>, opt(r.ecart, nf), ...(masque ? [] : [fmtDA(r.valeur_fin)])])}
      foot={masque ? [] : [["TOTAL", "", "", "", "", "", "", "", fmtDA(rows.reduce((a, r) => a + r.valeur_fin, 0))]]} />
  </Section>
);

const FacturesAnnees = ({ fa }) => (
  <Section title="Quantités facturées par produit et par année">
    <TblBase testid="etat-stock-factures-annees" head={["Produit", ...fa.annees.flatMap((y) => [`Qté facturée ${y}§`, `CA HT ${y}§`])]}
      rows={fa.produits.map((p) => [p.produit, ...fa.annees.flatMap((y) => [nf(p.annees[y].q), fmtDA(p.annees[y].ht)])])}
      foot={[["TOTAL", ...fa.annees.flatMap((y) => ["", fmtDA(fa.produits.reduce((a, p) => a + p.annees[y].ht, 0))])]]} />
  </Section>
);

const ValeurStock = ({ v, annee }) => (
  <Section title={`Valeur du stock au 31/12/${annee}`}>
    <div className="grid gap-3 sm:grid-cols-3 mb-4" data-testid="etat-stock-valeur-kpis">
      {[["Matières premières", v.total_mp, "mp"], ["Produits finis", v.total_pf, "pf"], ["Total stock valorisé HT", v.total, "total"]].map(([l, x, k]) => (
        <div key={k} className={`rounded-lg border p-3 ${k === "total" ? "bg-primary/5 border-primary/30" : ""}`}>
          <div className="text-xs text-muted-foreground">{l}</div>
          <div className="text-lg font-bold tabular" data-testid={`etat-stock-valeur-${k}`}>{fmtDA(x)}</div>
        </div>
      ))}
    </div>
    <TblBase testid="etat-stock-valeur-table" head={["Article", "Type", "Stock au 31/12§", "PU HT§", "Valeur HT§"]}
      rows={[...v.mp.map((x) => [x.famille, `Matière première (${x.unite})`, nf(x.stock), x.pu ? fmtDA(x.pu) : "—", fmtDA(x.valeur)]),
        ...v.pf.map((x) => [x.produit, "Produit fini", nf(x.stock), opt(x.pu, fmtDA), fmtDA(x.valeur)])]}
      foot={[["TOTAL", "", "", "", fmtDA(v.total)]]} />
    <p className="text-xs text-muted-foreground mt-2">Matières premières au coût moyen pondéré des achats de l'année (à défaut, prix de la recette) ; produits finis au coût de revient unitaire.</p>
  </Section>
);

const RetirerBtn = ({ p, onAction }) => onAction && (
  <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => onAction("retirer", p.produit)} data-testid={`etat-stock-retirer-${p.produit}`}>
    <Trash2 className="h-4 w-4" /> Supprimer
  </Button>
);

const Retires = ({ e, onAction }) => onAction && e.produits_retires?.length > 0 && (
  <Section title="Produits retirés de l'état de stock">
    <div className="divide-y" data-testid="etat-stock-retires">
      {e.produits_retires.map((r) => (
        <div key={r.produit} className="flex items-center justify-between py-2 text-sm">
          <span>{r.produit} <span className="text-xs text-muted-foreground">{r.retire_le ? `retiré le ${dfr(r.retire_le.slice(0, 10))}` : ""}</span></span>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => onAction("retablir", r.produit)} data-testid={`etat-stock-retablir-${r.produit}`}><RotateCcw className="h-4 w-4" /> Rétablir</Button>
        </div>
      ))}
    </div>
  </Section>
);

const EcartProd = ({ p, onAction }) => {
  const [v, setV] = useState(String(p.ecart_pct ?? 0));
  useEffect(() => { setV(String(p.ecart_pct ?? 0)); }, [p.ecart_pct]);
  const label = <span className="text-sm text-muted-foreground whitespace-nowrap">Écart de production{p.ecart_pct_specifique ? " (produit)" : " (année)"} :</span>;
  if (!onAction) return <div className="flex items-center gap-2" data-testid={`etat-stock-ecart-${p.produit}`}>{label}<b className="text-sm">{nf(p.ecart_pct)} %</b></div>;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`etat-stock-ecart-${p.produit}`}>
      {label}
      <Input className="h-8 w-20 text-right" inputMode="decimal" value={v} onChange={(ev) => setV(ev.target.value)} data-testid={`etat-stock-ecart-input-${p.produit}`} />
      <span className="text-sm">%</span>
      <Button size="sm" variant="outline" onClick={() => onAction("ecart", p.produit, v)} data-testid={`etat-stock-ecart-save-${p.produit}`}>Appliquer</Button>
      {p.ecart_pct_specifique && <Button size="sm" variant="ghost" onClick={() => onAction("ecart", p.produit, null)} data-testid={`etat-stock-ecart-reset-${p.produit}`}>Taux de l'année</Button>}
    </div>
  );
};

const QteCell = ({ p, m, onAction }) => {
  const [v, setV] = useState(String(m.qte_unit));
  useEffect(() => { setV(String(m.qte_unit)); }, [m.qte_unit]);
  if (!onAction) return nf(m.qte_unit);
  const save = () => {
    const n = Number(String(v).replace(",", "."));
    if (Number.isNaN(n) || n < 0) { setV(String(m.qte_unit)); return toast.error("Quantité invalide"); }
    if (n !== m.qte_unit) onAction("qte", p.produit, { mp: m.mp, qte: v });
  };
  return <Input className="h-7 w-24 ml-auto text-right" inputMode="decimal" value={v} onChange={(ev) => setV(ev.target.value)} onBlur={save}
    onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); }} data-testid={`etat-stock-qte-${p.produit}-${m.mp}`} />;
};

const PrixCell = ({ p, m, onAction }) => {
  const [v, setV] = useState(String(m.prix ?? 0));
  useEffect(() => { setV(String(m.prix ?? 0)); }, [m.prix]);
  if (!onAction) return m.prix ? fmtDA(m.prix) : "—";
  const save = () => {
    const n = Number(String(v).replace(",", "."));
    if (Number.isNaN(n) || n < 0) { setV(String(m.prix ?? 0)); return toast.error("Prix invalide"); }
    if (n !== m.prix) onAction("prix", p.produit, { mp: m.mp, prix: v });
  };
  return <Input className="h-7 w-28 ml-auto text-right" inputMode="decimal" value={v} onChange={(ev) => setV(ev.target.value)} onBlur={save}
    onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); }} data-testid={`etat-stock-prix-${p.produit}-${m.mp}`} />;
};

const EcartFacturation = ({ e }) => (
  <Section title={`Écart facturation / production par produit — ${e.annee}`}>
    <Tbl testid="etat-stock-ecart-facturation" head={["Produit", "Stock ouverture§", "Production§", "Facturé (ventes)§", "Utilisé en interne§", "Avarie + défaut§", "Écart prod. − facturé − interne − pertes§", "Stock final§"]}
      rows={e.produits.map((p) => { const pe = (p.avarie || 0) + (p.defaut || 0); const ec = p.production - p.vendus - (p.interne || 0) - pe; return [p.produit, nf(p.ouverture), nf(p.production), nf(p.vendus), p.interne ? nf(p.interne) : "—", pe ? nf(pe) : "—",
        <span key="e" className={ec < 0 ? "text-destructive font-semibold" : ec > 0 ? "text-amber-600 font-semibold" : ""}>{nf(ec)}</span>, nf(p.stock_final)]; })} foot={[]} />
    <p className="text-xs text-muted-foreground mt-2">Écart positif = produit non facturé (mis en stock) ; négatif = facturé au-delà de la production (pris sur le stock d'ouverture).</p>
  </Section>
);

const AjoutMatiere = ({ p, onAction }) => {
  const [f, setF] = useState({ mp: "", unite: "unité", qte: "", prix: "" });
  if (!onAction || !p.recette) return null;
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2" data-testid={`etat-stock-ajout-mp-${p.produit}`}>
      <Input className="h-8 w-56" placeholder="Matière / emballage (ex. Pot 250 g)" value={f.mp} onChange={set("mp")} data-testid="ajout-mp-nom" />
      <Input className="h-8 w-24" placeholder="Unité" value={f.unite} onChange={set("unite")} data-testid="ajout-mp-unite" />
      <Input className="h-8 w-28 text-right" placeholder="Qté / unité" inputMode="decimal" value={f.qte} onChange={set("qte")} data-testid="ajout-mp-qte" />
      <Input className="h-8 w-28 text-right" placeholder="Prix" inputMode="decimal" value={f.prix} onChange={set("prix")} data-testid="ajout-mp-prix" />
      <Button size="sm" variant="outline" onClick={() => onAction("ajout_mp", p.produit, f)} data-testid="ajout-mp-btn">Ajouter à la recette</Button>
    </div>
  );
};

const MpNom = ({ p, m, onAction }) => (
  <span className="inline-flex items-center gap-1">{m.mp}
    {onAction && <button type="button" className="text-muted-foreground hover:text-destructive" title="Retirer de la recette"
      onClick={() => window.confirm(`Retirer « ${m.mp} » de la recette ${p.produit} ?`) && onAction("suppr_mp", p.produit, { mp: m.mp })} data-testid={`etat-stock-suppr-mp-${p.produit}-${m.mp}`}><Trash2 className="h-3.5 w-3.5" /></button>}
  </span>
);

const PertesProd = ({ p, onAction }) => {
  const [a, setA] = useState(String(p.avarie_pct || 0));
  const [d, setD] = useState(String(p.defaut_pct || 0));
  useEffect(() => { setA(String(p.avarie_pct || 0)); setD(String(p.defaut_pct || 0)); }, [p.avarie_pct, p.defaut_pct]);
  if (!onAction) return <span className="text-sm text-muted-foreground" data-testid={`etat-stock-pertes-${p.produit}`}>Avarie : <b>{nf(p.avarie_pct || 0)} %</b> · Défaut de production : <b>{nf(p.defaut_pct || 0)} %</b></span>;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`etat-stock-pertes-${p.produit}`}>
      <span className="text-sm text-muted-foreground whitespace-nowrap">Avarie</span>
      <Input className="h-8 w-16 text-right" inputMode="decimal" value={a} onChange={(ev) => setA(ev.target.value)} data-testid={`etat-stock-avarie-input-${p.produit}`} /><span className="text-sm">%</span>
      <span className="text-sm text-muted-foreground whitespace-nowrap ml-2">Défaut de production</span>
      <Input className="h-8 w-16 text-right" inputMode="decimal" value={d} onChange={(ev) => setD(ev.target.value)} data-testid={`etat-stock-defaut-input-${p.produit}`} /><span className="text-sm">%</span>
      <Button size="sm" variant="outline" onClick={() => onAction("pertes", p.produit, { avarie: a, defaut: d })} data-testid={`etat-stock-pertes-save-${p.produit}`}>Appliquer</Button>
    </div>
  );
};

const ProduitCard = ({ p, onAction }) => (
  <Section title={`${p.produit}${p.recette ? ` — ${p.recette}` : ""}`}>
    <div data-testid="etat-stock-produit-card">
      <div className="flex flex-wrap items-center justify-between gap-2 -mt-2 mb-1"><div className="flex flex-wrap items-center gap-4"><EcartProd p={p} onAction={onAction} /><PertesProd p={p} onAction={onAction} /></div><RetirerBtn p={p} onAction={onAction} /></div>
      <Tbl head={["Stock ouverture§", "Production§", "Ventes§", "Utilisé en interne§", `Avarie (${nf(p.avarie_pct || 0)} %)§`, `Défaut prod. (${nf(p.defaut_pct || 0)} %)§`, "Écart inventaire§", "Stock final§", "CA HT§", "Coût revient / u§", "Coût de revient ventes§", "Marge brute§"]}
        rows={[[nf(p.ouverture), nf(p.production), nf(p.vendus), p.interne ? nf(p.interne) : "—", p.avarie ? nf(p.avarie) : "—", p.defaut ? nf(p.defaut) : "—", p.ecart ? `${nf(p.ecart)}${p.date_inventaire ? ` (inv. ${dfr(p.date_inventaire)})` : ""}` : "—", nf(p.stock_final), fmtDA(p.ht), opt(p.cout_unitaire, fmtDA), opt(p.cout_total, fmtDA), opt(p.marge, fmtDA)]]} foot={[]} />
      {p.matieres.length > 0 && (
        <div className="mt-4">
          <Tbl head={["Matière / emballage", "Unité", "Qté / unité§", "Prix§", "Coût / unité§", "Qté consommée (production)§", "Valeur HT§"]}
            rows={p.matieres.map((m) => [<MpNom key="n" p={p} m={m} onAction={onAction} />, m.unite, <QteCell key="q" p={p} m={m} onAction={onAction} />, <PrixCell key="p" p={p} m={m} onAction={onAction} />, fmtDA(m.cout_unit), nf(m.quantite), fmtDA(m.valeur)])}
            foot={[["TOTAL", "", "", "", fmtDA(p.cout_unitaire || 0), "", fmtDA(p.matieres.reduce((a, m) => a + m.valeur, 0))]]} />
        </div>
      )}
      <AjoutMatiere p={p} onAction={onAction} />
    </div>
  </Section>
);

const QteEntree = ({ x, i, onAction }) => {
  const [v, setV] = useState(String(x.quantite));
  useEffect(() => { setV(String(x.quantite)); }, [x.quantite]);
  if (!onAction) return nf(x.quantite);
  const save = () => {
    const n = Number(String(v).replace(",", "."));
    if (Number.isNaN(n) || n <= 0) { setV(String(x.quantite)); return toast.error("Quantité invalide"); }
    if (n !== x.quantite) onAction("entree_qte", null, { i, quantite: v });
  };
  return <Input className="h-7 w-28 ml-auto text-right" inputMode="decimal" value={v} onChange={(ev) => setV(ev.target.value)} onBlur={save}
    onKeyDown={(ev) => { if (ev.key === "Enter") ev.currentTarget.blur(); }} data-testid={`etat-stock-entree-qte-${i}`} />;
};

const EntreesBilan = ({ e, onAction }) => (
  <>
    {e.entrees.length > 0 && (
      <Section title="Entrées de matières premières importées (quantités rentrées en stock)">
        <Tbl testid="etat-stock-entrees-mp" head={["N°", "Date", "Matière première importée", "Réf. PPI", `Quantité rentrée (${e.unite_mp || "kg"})§`, `PU / ${e.unite_mp || "kg"}§`, "Montant§"]}
          rows={e.entrees.map((x, i) => [i + 1, dfr(x.date), x.designation, x.ref || "—", <QteEntree key="q" x={x} i={i} onAction={onAction} />, fmtDA(x.pu), fmtDA(x.montant)])}
          foot={[["", "", "TOTAL", "", nf(e.entrees_qte), "", fmtDA(e.entrees_montant)]]} />
      </Section>
    )}
    {e.bilan_mp.length > 0 && (
      <Section title="Stock de matières premières importées (ouverture + entrées − consommation)">
        <Tbl testid="etat-stock-bilan-mp" head={["Matière première importée", "Correspond à (recettes)", ...["Stock ouverture", "Entrées", "Consommation", "Écart d'inventaire", "Stock final"].map((h) => `${h} (${e.unite_mp || "kg"})§`)]}
          rows={e.bilan_mp.map((b) => [b.famille, b.mps.join(", "), nf(b.ouverture), nf(b.entrees), nf(b.consommation),
            b.ecart ? `${nf(b.ecart)}${b.date_inventaire ? ` (inv. ${dfr(b.date_inventaire)})` : ""}` : "—",
            <span key="s" className={b.stock_final < 0 ? "text-destructive font-semibold" : "font-semibold"}>{nf(b.stock_final)}</span>])} foot={[]} />
      </Section>
    )}
  </>
);

const StockNegatif = ({ e }) => {
  const neg = (e.bilan_mp || []).filter((b) => b.stock_final < 0);
  if (!neg.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-800 flex gap-3" data-testid="etat-stock-alerte-negatif">
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="text-sm">
        <b>Stock de matière première négatif</b> — la consommation dépasse le stock disponible :
        <ul className="mt-1 list-disc pl-5">{neg.map((b) => <li key={b.famille} data-testid={`etat-stock-negatif-${b.famille}`}>{b.famille} : <b>{nf(b.stock_final)} {e.unite_mp || "kg"}</b></li>)}</ul>
      </div>
    </div>
  );
};

const RecettesView = ({ e, vue, onAction }) => {
  const t = e.totaux;
  if (vue === "produit") {
    return (
      <>
        <p className="text-sm text-muted-foreground" data-testid="etat-stock-note">{e.note}</p>
        <StockNegatif e={e} />
        <EcartFacturation e={e} />
        {e.produits.map((p) => <ProduitCard key={p.produit} p={p} onAction={onAction} />)}
        <EntreesBilan e={e} onAction={onAction} />
        <Retires e={e} onAction={onAction} />
      </>
    );
  }
  return (
    <>
      <p className="text-sm text-muted-foreground" data-testid="etat-stock-note">{e.note}</p>
      <StockNegatif e={e} />
      <EcartFacturation e={e} />
      <Section title="1. Produits finis : production, ventes et stock">
        <Tbl testid="etat-stock-produits" head={["Produit", "Stock ouverture§", "Production§", "Ventes§", "Interne§", "Avarie§", "Défaut prod.§", "Écart inv.§", "Stock final§", "CA HT§", "Coût revient / u§", "Coût de revient total§", "Marge brute§"]}
          rows={e.produits.map((p) => [<span key="n" className="inline-flex items-center gap-1">{p.produit}<RetirerBtn p={p} onAction={onAction} /></span>, nf(p.ouverture), nf(p.production), nf(p.vendus), p.interne ? nf(p.interne) : "—", p.avarie ? nf(p.avarie) : "—", p.defaut ? nf(p.defaut) : "—", p.ecart ? nf(p.ecart) : "—", nf(p.stock_final), fmtDA(p.ht),
            opt(p.cout_unitaire, fmtDA), opt(p.cout_total, fmtDA), opt(p.marge, fmtDA)])}
          foot={[["TOTAL", nf(t.ouverture), nf(t.production), nf(t.vendus), "", t.avarie ? nf(t.avarie) : "—", t.defaut ? nf(t.defaut) : "—", t.ecart ? nf(t.ecart) : "—", <span key="s" data-testid="etat-stock-final-value">{nf(t.stock_final)}</span>, fmtDA(t.ht), "", fmtDA(t.cout_total), fmtDA(t.marge)]]} />
      </Section>
      <Section title="2. Matières premières et emballages consommés (d'après la production)">
        <Tbl testid="etat-stock-matieres" head={["Matière première / emballage", "Unité", "Quantité consommée§", "PU moyen HT§", "Valeur HT§"]}
          rows={e.matieres.map((m) => [m.mp, m.unite, nf(m.quantite), m.pu ? fmtDA(m.pu) : "—", fmtDA(m.valeur)])}
          foot={[["TOTAL", "", "", "", fmtDA(t.mp_valeur)]]} />
      </Section>
      <EntreesBilan e={e} onAction={onAction} />
      <Retires e={e} onAction={onAction} />
    </>
  );
};

export default function EtatStock() {
  const { societes, societeId } = useApp();
  const [sid, setSid] = useState("");
  const [annees, setAnnees] = useState([]);
  const [annee, setAnnee] = useState("");
  const [e, setE] = useState(null);
  const [vue, setVue] = useState("produit");
  const [masque, setMasque] = useState(() => localStorage.getItem("etat-stock-masque") === "1");
  const toggleMasque = () => setMasque((m) => { localStorage.setItem("etat-stock-masque", m ? "0" : "1"); return !m; });
  const [reload, setReload] = useState(0);
  const { role } = useAuth();
  const onAction = role === "administrateur" ? async (action, produit, val) => {
    if (action === "retirer" && !window.confirm(`Retirer « ${produit} » de l'état de stock ? Sa recette et sa consommation de matières premières seront retirées (les factures ne changent pas).`)) return;
    try {
      if (action === "pertes") {
        const av = Number(String(val.avarie).replace(",", ".")), df = Number(String(val.defaut).replace(",", "."));
        if ([av, df].some((x) => Number.isNaN(x) || x < 0 || x > 100)) return toast.error("Pourcentage invalide (entre 0 et 100 %)");
        await api.put("/etat-stock/pertes", { societe_id: sid, annee: Number(annee), produit, avarie: av, defaut: df });
        toast.success("Avarie et défaut de production mis à jour");
        return setReload((n) => n + 1);
      }
      if (action === "ajout_mp" || action === "suppr_mp") {
        const body = action === "suppr_mp" ? { mp: val.mp, supprimer: true }
          : { mp: val.mp, unite: val.unite, qte: Number(String(val.qte).replace(",", ".")), prix: Number(String(val.prix).replace(",", ".")) };
        await api.put("/etat-stock/recette-ingredient", { societe_id: sid, produit, ...body });
        toast.success(action === "ajout_mp" ? `« ${val.mp} » ajouté à la recette` : `« ${val.mp} » retiré de la recette`);
        return setReload((n) => n + 1);
      }
      if (action === "entree_qte") {
        const q = Number(String(val.quantite).replace(",", "."));
        if (Number.isNaN(q) || q <= 0) return toast.error("Quantité invalide");
        const entrees = e.entrees.map((x, j) => ({ date: x.date, designation: x.designation, famille: x.famille, ref: x.ref, montant: x.montant, quantite: j === val.i ? q : x.quantite }));
        await api.put("/etat-stock/entrees", { societe_id: sid, annee: Number(annee), entrees });
        toast.success("Quantité rentrée en stock mise à jour");
        return setReload((n) => n + 1);
      }
      if (action === "prix") {
        const pr = Number(String(val.prix).replace(",", "."));
        if (Number.isNaN(pr) || pr < 0) return toast.error("Prix invalide");
        await api.put("/etat-stock/recette-prix", { societe_id: sid, annee: Number(annee), produit, mp: val.mp, prix: pr });
        toast.success(`Coût de « ${val.mp} » mis à jour pour ${produit} (${annee})`);
        return setReload((n) => n + 1);
      }
      if (action === "qte") {
        const q = Number(String(val.qte).replace(",", "."));
        if (Number.isNaN(q) || q < 0) return toast.error("Quantité invalide");
        await api.put("/etat-stock/recette-qte", { societe_id: sid, annee: Number(annee), produit, mp: val.mp, qte: q });
        toast.success(`Consommation de « ${val.mp} » mise à jour pour ${annee}`);
        return setReload((n) => n + 1);
      }
      if (action === "ecart") {
        const pct = val === null ? null : Number(String(val).replace(",", "."));
        if (pct !== null && Number.isNaN(pct)) return toast.error("Écart invalide");
        await api.put("/etat-stock/ecart-produit", { societe_id: sid, annee: Number(annee), produit, pct });
        toast.success("Écart de production mis à jour");
        return setReload((n) => n + 1);
      }
      await api.post(`/etat-stock/produits/${action}`, { societe_id: sid, produit });
      toast.success(action === "retirer" ? "Produit retiré de l'état de stock" : "Produit rétabli");
      setReload((n) => n + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  } : null;

  useEffect(() => {
    const cs = societes.find((s) => /NEGOCE/i.test(s.name));
    setSid(societeId !== "all" ? societeId : (cs?.id || ""));
  }, [societeId, societes]);

  useEffect(() => {
    if (!sid) return;
    api.get("/etat-stock/annees", { params: { societe_id: sid } }).then((r) => {
      setAnnees(r.data);
      setAnnee((a) => (r.data.includes(Number(a)) ? a : String(r.data[0] || "")));
    });
  }, [sid]);

  useEffect(() => {
    setE(null);
    if (sid && annee) api.get("/etat-stock", { params: { societe_id: sid, annee } }).then((r) => setE(r.data)).catch(() => setE(false));
  }, [sid, annee, reload]);

  const t = e?.totaux;
  return (
    <div data-testid="etat-stock-page">
      <PageHeader title="État de stock" subtitle={societes.find((s) => s.id === sid)?.name}
        actions={<div className="flex flex-wrap items-center justify-end gap-2 max-w-4xl">
          {e?.mode === "recettes" && (
            <div className="inline-flex rounded-lg border p-0.5" data-testid="etat-stock-vue-toggle">
              {[["produit", "Par produit"], ["global", "Tous les produits"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`etat-stock-vue-${v}`} onClick={() => setVue(v)}
                  className={`px-3 h-8 text-sm rounded-md whitespace-nowrap transition-colors ${vue === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
              ))}
            </div>
          )}
          {e?.mode === "recettes" && role === "administrateur" && <PrixMPDialog sid={sid} onSaved={() => setReload((n) => n + 1)} />}
          {e?.mode === "recettes" && role === "administrateur" && <PrixVenteDialog sid={sid} />}
          {e?.mode === "recettes" && role === "administrateur" && <EntreesMPDialog sid={sid} annee={annee} entrees={e.entrees} unite={e.unite_mp} onSaved={() => setReload((n) => n + 1)} />}
          {e?.mode === "recettes" && (
            <Button variant="outline" className="gap-2" onClick={toggleMasque} data-testid="etat-stock-masque-btn">
              {masque ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}{masque ? "Afficher coûts et marges" : "Masquer coûts et marges"}
            </Button>
          )}
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="w-32" data-testid="etat-stock-annee"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent className="bg-popover">{annees.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
          {e?.mode === "recettes" && (
            <Button variant="outline" className="gap-2" data-testid="etat-stock-pdf-ecarts-btn"
              onClick={() => downloadPdf(`/etat-stock/pdf?societe_id=${sid}&annee=${annee}&vue=ecarts`, `ecart-facturation-production-${annee}`)}>
              <FileDown className="h-4 w-4" /> PDF écart facturation
            </Button>
          )}
          {e?.mode === "recettes" && (
            <Button variant="outline" className="gap-2" data-testid="etat-stock-pdf-inventaire-btn"
              onClick={() => downloadPdf(`/etat-stock/inventaire-pdf?societe_id=${sid}&annee=${annee}${masque ? "&masquer_couts=1" : ""}`, `inventaire-${annee}`)}>
              <FileDown className="h-4 w-4" /> PDF inventaire
            </Button>
          )}
          {e?.mode === "recettes" && (
            <Button variant="outline" className="gap-2" data-testid="etat-stock-pdf-produits-finis-btn"
              onClick={() => downloadPdf(`/etat-stock/pdf?societe_id=${sid}&annee=${annee}&vue=produits_finis${masque ? "&masquer_couts=1" : ""}`, `etat-stock-produits-finis-${annee}`)}>
              <FileDown className="h-4 w-4" /> PDF produits finis
            </Button>
          )}
          {e?.mode === "recettes" && (
            <Button variant="outline" className="gap-2" data-testid="etat-stock-pdf-valorise-btn"
              onClick={() => downloadPdf(`/etat-stock/pdf?societe_id=${sid}&annee=${annee}&vue=valorise`, `etat-stock-valorise-${annee}`)}>
              <FileDown className="h-4 w-4" /> PDF stock valorisé
            </Button>
          )}

          <Button className="gap-2" disabled={!e} data-testid="etat-stock-pdf-btn"
            onClick={() => downloadPdf(`/etat-stock/pdf?societe_id=${sid}&annee=${annee}&vue=${vue}${masque ? "&masquer_couts=1" : ""}`, `etat-stock-${annee}`)}>
            <FileDown className="h-4 w-4" /> PDF
          </Button>
        </div>} />
      {annees.length === 0 && <EmptyState title="Aucun état de stock" description="Aucun état de stock n'est disponible pour cette société." />}
      {e === null && annees.length > 0 && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {e && e.mode === "recettes" && !masque && e.valorisation && <ValeurStock v={e.valorisation} annee={annee} />}
      {e && e.mode === "recettes" && <MasqueCtx.Provider value={masque}><RecettesView e={e} vue={vue} onAction={onAction} /></MasqueCtx.Provider>}
      {e && e.mode !== "recettes" && (
        <>
          <p className="text-sm text-muted-foreground" data-testid="etat-stock-note">
            {e.note} · Perte de {nf(e.perte_pct)} % de matière première (préparation alimentaire) dans le process de production.{e.stock_ouverture ? ` · ${e.stock_ouverture}` : ""}
          </p>
          <Section title="1. Entrées de matière première">
            <Tbl testid="etat-stock-entrees" head={["N°", "Date", "Désignation MP", "Quantité§", "U. mesure", "PU / litre§", "Montant§"]}
              rows={e.entrees.map((x, i) => [i + 1, dfr(x.date), x.designation, nf(x.quantite), x.unite, fmtDA(x.pu), fmtDA(x.montant)])}
              foot={[["", "", "Total MP importée", nf(t.entrees_qte), "Litre", "", fmtDA(t.entrees_montant)],
                ["", "", `Perte estimée ${nf(e.perte_pct)} %`, `-${nf(t.perte)}`, "Litre", "", ""],
                ["", "", "Total MP utilisable production", nf(t.utilisable), "Litre", "", ""]]} />
          </Section>
          <Section title="2. Production stockée">
            <Tbl testid="etat-stock-production" head={["Période", "Désignation", "Quantité pots§", "Quantité en litres§", "PU / pot§", "Coût de production§"]}
              rows={e.production.map((x) => [x.periode, x.designation, nf(x.pots), nf(x.litres), x.pu_pot ? fmtDA(x.pu_pot) : "—", x.cout ? fmtDA(x.cout) : "—"])}
              foot={[["TOTAL", "", nf(t.prod_pots), nf(t.prod_litres), "", t.prod_cout ? fmtDA(t.prod_cout) : "—"]]} />
          </Section>
          <Section title="3. Production vendue (d'après les factures de vente)">
            <Tbl testid="etat-stock-vendus" head={["Mois", "Désignation", "Quantité facturée§", "Équivalent pots 250 g§", "CA HT§"]}
              rows={e.vendus.map((x) => [x.mois, x.designation, nf(x.quantite), nf(x.pots), fmtDA(x.ht)])}
              foot={[["TOTAL", "", "", nf(t.vendus_pots), fmtDA(t.vendus_ht)]]} />
            <p className="text-xs text-muted-foreground mt-2">Équivalent pots : 1 carton de 10 kg = 40 pots de 250 g.</p>
          </Section>
          <Section title="4. Stock final de pots (production moins ventes)">
            <Tbl testid="etat-stock-final" head={["Libellé", "Quantité pots 250 g§"]}
              rows={[["Stock d'ouverture", nf(t.ouverture_pots)], ["+ Production stockée", nf(t.prod_pots)], ["− Production vendue", nf(t.vendus_pots)]]}
              foot={[[<span key="l" className={t.stock_final_pots < 0 ? "text-destructive" : ""}>STOCK FINAL{t.stock_final_pots < 0 ? " (écart : ventes supérieures à la production)" : ""}</span>,
                <span key="v" data-testid="etat-stock-final-value" className={t.stock_final_pots < 0 ? "text-destructive" : "text-success"}>{nf(t.stock_final_pots)}</span>]]} />
          </Section>
        </>
      )}
      {e && e.mode === "recettes" && e.stocks_annexes && (
        <StocksAnnexes rows={e.stocks_annexes} annee={annee} masque={masque}
          edit={role === "administrateur" && <StocksAnnexesDialog sid={sid} annee={annee} rows={e.stocks_annexes} onSaved={() => setReload((n) => n + 1)} />} />
      )}
      {e && e.mode === "recettes" && e.factures_annees && <FacturesAnnees fa={e.factures_annees} />}
    </div>
  );
}
